/**
 * ffmpeg.wasm を動的ロードするヘルパ (ブラウザ専用)。
 *
 * 戦略:
 *   - `@ffmpeg/ffmpeg` を dynamic import (アプリ起動時は読み込まない)
 *   - core (~30MB) は jsDelivr → unpkg の順にフォールバックで取得
 *   - `@ffmpeg/ffmpeg@0.12` の Module Worker は内部で `await import(coreURL)`
 *     を呼ぶため、**ESM 版 (`/dist/esm/`)** を指定する必要がある。
 *     UMD 版を渡すと `ERROR_IMPORT_FAILURE` になる。
 *   - 公式の `toBlobURL` は blob URL で `/umd/` → `/esm/` 置換が効かなくなる
 *     罠があるので、直接 CDN URL を渡す方式にする (CORS 設定は両 CDN で OK)。
 *   - ブラウザ必須条件: SharedArrayBuffer + Cross-Origin Isolation
 *     (`public/_headers` で COOP: same-origin + COEP: require-corp)
 */

import type { FFmpeg } from '@ffmpeg/ffmpeg';

import type { FFmpegHandle } from './video-builder';

const CORE_VERSION = '0.12.6';
/** ESM 版を指定 (Module Worker の import() 要件)。プライマリ + フォールバック。 */
const CORE_CDN_URLS: readonly string[] = [
  `https://cdn.jsdelivr.net/npm/@ffmpeg/core@${CORE_VERSION}/dist/esm`,
  `https://unpkg.com/@ffmpeg/core@${CORE_VERSION}/dist/esm`,
];

export interface LoadFFmpegOptions {
  readonly onProgress?: (ratio: number) => void;
  /** テスト用に CDN を差し替え可能 (配列で渡せば順に試行) */
  readonly coreBaseUrls?: readonly string[];
}

/**
 * ffmpeg.wasm をロードして、使える FFmpegHandle を返す。
 */
export async function loadFFmpeg(options: LoadFFmpegOptions = {}): Promise<FFmpegHandle> {
  assertCrossOriginIsolated();

  const { FFmpeg } = await import('@ffmpeg/ffmpeg');
  const baseUrls = options.coreBaseUrls ?? CORE_CDN_URLS;
  const ffmpeg: FFmpeg = new FFmpeg();
  ffmpeg.on('log', ({ message }) => {
    if (message) {
      // eslint-disable-next-line no-console
      console.debug('[ffmpeg]', message);
    }
  });

  options.onProgress?.(0);
  await loadWithFallback(ffmpeg, baseUrls, options.onProgress);
  options.onProgress?.(1);

  return { ffmpeg, loaded: true };
}

// ===== 内部ヘルパ ========================================================

/**
 * COOP + COEP による Cross-Origin Isolation が有効か確認。
 * 無効だと SharedArrayBuffer が使えず、ffmpeg.wasm v0.12 はロード失敗する。
 */
function assertCrossOriginIsolated(): void {
  if (typeof window !== 'undefined' && window.crossOriginIsolated === false) {
    throw new Error(
      'ブラウザが Cross-Origin Isolation されていません。Cloudflare Pages の COOP/COEP ヘッダが反映されているか確認してください。ブラウザのハード再読み込み (Cmd+Shift+R) で改善することがあります。',
    );
  }
}

/**
 * CDN URL を順に試し、どれかで ffmpeg.load() が成功するまでフォールバック。
 */
async function loadWithFallback(
  ffmpeg: FFmpeg,
  baseUrls: readonly string[],
  onProgress?: (r: number) => void,
): Promise<void> {
  const errors: string[] = [];
  let attempt = 0;
  for (const baseURL of baseUrls) {
    attempt += 1;
    try {
      onProgress?.(0.1 + (attempt - 1) * 0.2);
      await ffmpeg.load({
        coreURL: `${baseURL}/ffmpeg-core.js`,
        wasmURL: `${baseURL}/ffmpeg-core.wasm`,
      });
      onProgress?.(0.95);
      return;
    } catch (err) {
      errors.push(`${baseURL}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
  throw new Error(
    `ffmpeg.load() が全 CDN で失敗しました (crossOriginIsolated=${
      typeof window !== 'undefined' ? String(window.crossOriginIsolated) : 'n/a'
    })。試行 ${errors.length} 件:\n${errors.join('\n')}`,
  );
}
